import { toCustomerError } from './customerError';

/**
 * Normalize Stripe Connect fields from a venue record (venue-scoped, not per-user).
 */
export const extractStripeConnectFromVenue = (venue) => {
  if (!venue || typeof venue !== 'object') return null;

  const nested = venue.stripe_connect || venue.stripeConnect || venue.payments?.stripe || {};

  const stripeAccountId =
    nested.stripe_account_id ||
    nested.account_id ||
    venue.stripe_account_id ||
    venue.stripe_connect_account_id ||
    null;

  const onboardingStatus =
    nested.stripe_onboarding_status ||
    nested.onboarding_status ||
    venue.stripe_onboarding_status ||
    venue.stripe_connect_status ||
    null;

  const chargesEnabled = nested.charges_enabled ?? venue.stripe_charges_enabled ?? venue.charges_enabled;
  const payoutsEnabled = nested.payouts_enabled ?? venue.stripe_payouts_enabled ?? venue.payouts_enabled;

  if (
    !stripeAccountId &&
    !onboardingStatus &&
    chargesEnabled == null &&
    payoutsEnabled == null
  ) {
    return null;
  }

  return {
    stripe_account_id: stripeAccountId,
    stripe_onboarding_status: onboardingStatus,
    charges_enabled: Boolean(chargesEnabled),
    payouts_enabled: Boolean(payoutsEnabled),
    stripe_account_email:
      nested.stripe_account_email || nested.email || venue.stripe_account_email || venue.stripe_email || null,
    requirements_due: nested.requirements_due || venue.stripe_requirements_due || null
  };
};

export const hasVenueStripeConnection = (connect) => {
  if (!connect) return false;
  const onboarding = String(connect.stripe_onboarding_status || '').toLowerCase();
  if (onboarding === 'active') return true;
  if (connect.charges_enabled && connect.payouts_enabled) return true;
  if (connect.stripe_account_id || connect.account_id) return true;
  return false;
};

const isSessionExpiredMessage = (message = '') => {
  const text = String(message || '').toLowerCase();
  return (
    text.includes('token expired') ||
    text.includes('session has expired') ||
    text.includes('session is missing or expired') ||
    text.includes('log in again') ||
    text.includes('not authorised') ||
    text.includes('not authorized') ||
    text.includes('unauthor') ||
    text.includes('401')
  );
};

/** In-memory cache per venue — survives Payments tab unmount/remount. */
const STRIPE_CONNECT_CACHE_TTL_MS = 5 * 60 * 1000;
const stripeConnectCacheByVenue = new Map();

export const getCachedStripeConnectStatus = (venueId) => {
  const key = venueId != null ? String(venueId) : '';
  if (!key) return null;
  const entry = stripeConnectCacheByVenue.get(key);
  if (!entry) return null;
  if (Date.now() - entry.fetchedAt > STRIPE_CONNECT_CACHE_TTL_MS) {
    stripeConnectCacheByVenue.delete(key);
    return null;
  }
  return entry;
};

export const setCachedStripeConnectStatus = (venueId, payload) => {
  const key = venueId != null ? String(venueId) : '';
  if (!key || !payload) return;
  stripeConnectCacheByVenue.set(key, {
    ...payload,
    fetchedAt: Date.now()
  });
};

export const clearCachedStripeConnectStatus = (venueId) => {
  const key = venueId != null ? String(venueId) : '';
  if (!key) return;
  stripeConnectCacheByVenue.delete(key);
};

/**
 * Prefer payments API status; fall back to venue record so all dashboard users
 * see the same connected state for a venue.
 */
export const loadVenueStripeConnectStatus = async ({
  venueId,
  venueDetails,
  fetchVenueDetails,
  fetchConnectStatus,
  forceRefresh = false
}) => {
  const cacheKey = venueId != null ? String(venueId) : '';
  if (!forceRefresh && cacheKey) {
    const cached = getCachedStripeConnectStatus(cacheKey);
    if (cached) {
      return {
        status: cached.status,
        usedVenueFallback: Boolean(cached.usedVenueFallback),
        info: cached.info || '',
        fromCache: true,
        apiError: null
      };
    }
  }

  let apiStatus = null;
  let apiError = null;

  try {
    apiStatus = await fetchConnectStatus(venueId);
  } catch (error) {
    apiError = error;
  }

  let venueRecord = venueDetails;
  const venueFromProps = extractStripeConnectFromVenue(venueRecord);

  if (!hasVenueStripeConnection(venueFromProps) && typeof fetchVenueDetails === 'function') {
    try {
      venueRecord = await fetchVenueDetails(venueId);
    } catch {
      // ignore — use props only
    }
  }

  const venueStatus = extractStripeConnectFromVenue(venueRecord);

  if (hasVenueStripeConnection(venueStatus)) {
    const apiShowsConnection = hasVenueStripeConnection(apiStatus);
    if (!apiShowsConnection) {
      const result = {
        status: {
          ...venueStatus,
          ...(apiStatus && typeof apiStatus === 'object' ? apiStatus : {})
        },
        usedVenueFallback: true,
        apiError,
        fromCache: false
      };
      setCachedStripeConnectStatus(cacheKey, {
        status: result.status,
        usedVenueFallback: result.usedVenueFallback,
        info: 'Stripe is connected for this venue. Status is shared for everyone with dashboard access (owners and admins).'
      });
      return result;
    }
  }

  if (apiStatus) {
    const result = { status: apiStatus, usedVenueFallback: false, apiError: null, fromCache: false };
    setCachedStripeConnectStatus(cacheKey, {
      status: result.status,
      usedVenueFallback: false,
      info: ''
    });
    return result;
  }

  if (apiError) {
    throw apiError;
  }

  const empty = { status: null, usedVenueFallback: false, apiError: null, fromCache: false };
  setCachedStripeConnectStatus(cacheKey, { status: null, usedVenueFallback: false, info: '' });
  return empty;
};

export const formatStripeConnectLoadError = (message = '') => {
  if (isSessionExpiredMessage(message)) {
    return 'Your Occasia login session has expired. Sign out and log in again, then retry connecting Stripe.';
  }
  return toCustomerError(message, { fallback: 'Unable to load Stripe status.' });
};
